import { useState, useEffect, useRef } from 'react';
import useJeMobil from '../hooks/useJeMobil';
import {
    Banknote,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import TabBar from '../components/layout/TabBar';
import { muze, zalozkyModulu } from '../lib/moduly';
import { cardStyle as buildCardStyle } from '../lib/uiTokens';
import PrehledTab from './prehled/PrehledTab';
import DokladyModul from './doklady/DokladyModul';
import FinanceAop from './FinanceAop';
import FinanceDph from './FinanceDph';
import FinanceBanka from './FinanceBanka';
import PokladnaModul from './pokladna/PokladnaModul';
import { REZERVA_LISTY } from '../components/MobilniLista';
import DodavateleTab from './finance/DodavateleTab';
import ObdobiNavigator from './finance/ObdobiNavigator';

const SECTION_COLOR = '#3B82F6';
const SECTION_BG    = 'rgba(59,130,246,0.12)';

const MESICE = [
    'Leden','Únor','Březen','Duben','Květen','Červen',
    'Červenec','Srpen','Září','Říjen','Listopad','Prosinec'
];

// Záložky Financí = podstránky modulu v katalogu; kdo je vidí, řídí přístupy
// (šablona + výjimky), ne role. Kreslí je TabBar, seznam tady byl kopie.

export default function Finance({ user, initialTab = null, initialDokladId = null, initialDodavatelId = null, onNavigate }) {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const isMobile = useJeMobil();

    // Záložky kreslí TabBar z katalogu; tady jsou proto, aby se vyžádaná
    // záložka (stará route, proklik z toastu) dala ověřit proti přístupům.
    const tabs = zalozkyModulu('finance', user);
    // Stav měsíce (uzávěrka) vydá backend zatím jen CEO — role tu rozhoduje o datech.
    const jeCeo = user?.role === 'superadmin';

    // Výchozí tab: buď vyžádaný (stará route /pokladna), jinak první dostupný
    // pro danou roli — administrátor vidí jen Doklady a Pokladnu.
    // Výchozí: buď vyžádaná (stará route /pokladna, proklik z toastu), jinak
    // ji dosadí TabBar — první, ke které má uživatel přístup.
    const [activeTab, setActiveTab] = useState(() => (
        initialTab && tabs.some(t => t.id === initialTab) ? initialTab : null
    ));
    // Doklad vyžádaný z Banky (klik na odkaz u pohybu) — přepne na tab Doklady a otevře ho
    const [bankaOpenDokladId, setBankaOpenDokladId] = useState(null);
    const otevriDokladZBanky = (id) => { setBankaOpenDokladId(id); setActiveTab('doklady'); };
    // Přejetí prstem mezi záložkami je pryč: Finance mají osm sekcí a vodorovné
    // tahy uvnitř nich (pruh pohledů, tabulky, inspector) se s ním praly — člověk
    // posunul obsah a skončil v jiné sekci. Přepíná se středem spodní lišty.

    // Vyžádaný tab může dorazit i za běhu (klik na toast „Nový doklad", když už
    // jsou Finance otevřené) — komponenta se neremountuje, takže přepnout ručně.
    useEffect(() => {
        if (initialTab && tabs.some(t => t.id === initialTab)) setActiveTab(initialTab);
        // eslint-disable-next-line
    }, [initialTab]);
    const [rok, setRok] = useState(new Date().getFullYear());
    const [mesic, setMesic] = useState(new Date().getMonth() + 1);
    const [stavMesice, setStavMesice] = useState('otevreny');
    const [uzavirani, setUzavirani] = useState(false);
    const [variantaId, setVariantaId] = useState(null); // AOP: aktivní varianta (scénář)

    const token = document.querySelector('meta[name="csrf-token"]')?.content;

    const cardStyle = buildCardStyle(isDark);

    // Načtení stavu měsíce
    useEffect(() => {
        if (!jeCeo) return;
        fetch(`/api/finance/vysledovka?rok=${rok}&mesic=${mesic}`)
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (data) setStavMesice(data.stav_mesice || 'otevreny');
            })
            .catch(() => {});
    }, [rok, mesic, jeCeo]);

    const handleUzavritMesic = async () => {
        if (!confirm(`Opravdu uzavřít ${MESICE[mesic - 1]} ${rok}?`)) return;
        setUzavirani(true);
        try {
            const r = await fetch(`/api/finance/mesic/${rok}/${mesic}/uzavrit`, {
                method: 'POST',
                headers: { 'X-CSRF-TOKEN': token },
            });
            if (r.ok) setStavMesice('uzavreny');
        } catch (e) { console.error(e); }
        setUzavirani(false);
    };

    const handleZnovuOtevrit = async () => {
        if (!confirm(`Opravdu znovu otevřít ${MESICE[mesic - 1]} ${rok}?`)) return;
        try {
            const r = await fetch(`/api/finance/mesic/${rok}/${mesic}/znovu-otevrit`, {
                method: 'POST',
                headers: { 'X-CSRF-TOKEN': token },
            });
            if (r.ok) setStavMesice('otevreny');
        } catch (e) { console.error(e); }
    };

    // Spodní hrana fixní hlavičky → topOffset pro inspector dokladu, aby vyjel
    // pod taby a hlavička modulu zůstala vidět i s otevřeným inspektorem.
    const pageHeaderRef = useRef(null);
    const [headerBottom, setHeaderBottom] = useState(0);
    useEffect(() => {
        const el = pageHeaderRef.current;
        if (!el) return;
        const update = () => {
            const rect = el.getBoundingClientRect();
            setHeaderBottom(Math.max(0, Math.round(rect.bottom)));
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        window.addEventListener('resize', update);
        return () => { ro.disconnect(); window.removeEventListener('resize', update); };
    }, []);

    return (
        // Stránka je flex sloupec na plnou výšku <main>. Roluje POUZE spodní blok
        // s obsahem — díky tomu obsah končí přesně pod menu a nikde nad ním
        // neprobleskne. Hlavička je normální (nepozicovaná) část stránky, takže
        // sdílí pozadí stránky a nepotřebuje vlastní podklad ani z-index.
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Hlavička + taby + kontextové ovládání — nikdy neroluje.
                Měříme spodní hranu (pageHeaderRef), aby inspector dokladu
                vyjížděl pod ní a nepřekryl taby. */}
            <div
                ref={pageHeaderRef}
                className="px-4 md:px-6 pt-4 md:pt-6"
                style={{ flexShrink: 0 }}
            >
            {/* Hlavička jen na desktopu — na mobilu říká střed lišty, kde jsem,
                a pruh nahoře by tu informaci zopakoval za 62 px obrazovky. */}
            {!isMobile && (
                <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: SECTION_BG }}>
                        <Banknote size={22} style={{ color: SECTION_COLOR }} />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Finance</h1>
                        <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>P&L výsledovka, doklady, dodavatelé, AOP plánování</p>
                    </div>
                </div>
            )}

            {/* Záložky drží hlavička. Ovládání období se přesunulo pod menu do
                obsahu (ObdobiNavigator, sjednoceno s Výsledovkou), takže výška
                hlavičky je konstantní na všech záložkách. */}
            <TabBar modul="finance" user={user} aktivni={activeTab} onZmena={setActiveTab}
                barva={SECTION_COLOR} isDark={isDark} isMobile={isMobile} />
            </div>

            {/* Tab content — jediná rolující část stránky. minHeight:0 je nutné,
                jinak by flex položka nešla zmenšit pod výšku obsahu a scroll by
                se přelil na <main>. */}
            <div
                className="px-4 md:px-6 pt-5 pb-8"
                style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
            >
                {/* Ovládání období pod menu — sjednocené s Výsledovkou.
                    Doklady = seznam (bez měsíce), Výsledovka + Pokladna si řídí
                    období samy (vlastní navigátor / živý dashboard) → navigátor nemají.
                    Uzávěrka měsíce se přesunula do Výsledovky (vedle Dopočet toggle). */}
                {/* DPH — navigátor a obsah tvoří jeden sloupec na výšku stránky
                    (od xl výš), aby stránka sama nerolovala a scrollovaly jen
                    tabulky / panel uvnitř. Pod xl se layout zalomí a roluje běžně. */}
                {activeTab === 'dph' && (
                    <div className="flex flex-col xl:h-full xl:min-h-0">
                        <div className="flex-shrink-0">
                            <ObdobiNavigator
                                mode="mesic" rok={rok} setRok={setRok} mesic={mesic} setMesic={setMesic}
                                isAdmin={muze(user, 'finance.dph', 'upravuje')} stavMesice={stavMesice} uzavirani={uzavirani}
                                onUzavrit={handleUzavritMesic} onZnovuOtevrit={handleZnovuOtevrit}
                            />
                        </div>
                        <div className="xl:flex-1 xl:min-h-0">
                            <FinanceDph rok={rok} mesic={mesic} cardStyle={cardStyle} isDark={isDark} />
                        </div>
                    </div>
                )}
                {/* Výsledovka si období řídí sama (vlastní ym + ObdobiNavigator),
                    ale isMobile potřebuje: na telefonu se P&L přeskládá do bloků
                    a pořadí se obrací (verdikt a „K dořešení" před tabulkou).
                    Do 3.480.0 ho nedostávala vůbec, takže se na mobilu kreslila
                    desktopová tabulka o sedmi sloupcích, která ujela z displeje. */}
                {activeTab === 'vysledovka' && (
                    <div className="flex flex-col"
                        style={isMobile
                            ? { position: 'fixed', zIndex: 0, top: 'var(--hlavicka-mobil)', left: 0, right: 0, bottom: 0, overflow: 'hidden' }
                            : { height: '100%', minHeight: 0 }}>
                        <PrehledTab isDark={isDark} user={user} isMobile={isMobile}
                            rezervaDole={isMobile ? REZERVA_LISTY : null} />
                    </div>
                )}
                {/* Doklady jsou od 3.448 třísloupcový modul jako Pokladna —
                    stejný obal (výšku drží modul, scrollují sloupce) i stejné
                    připnutí k výřezu na mobilu. */}
                {activeTab === 'doklady' && (
                    <div className="flex flex-col"
                        style={isMobile
                            ? { position: 'fixed', zIndex: 0, top: 'var(--hlavicka-mobil)', left: 0, right: 0, bottom: 0, overflow: 'hidden' }
                            : { height: '100%', minHeight: 0 }}>
                        <DokladyModul user={user} isDark={isDark} isMobile={isMobile}
                            rok={rok} mesic={mesic}
                            rezervaDole={isMobile ? REZERVA_LISTY : null}
                            initialDokladId={bankaOpenDokladId ?? initialDokladId} />
                    </div>
                )}
                {/* Banka je od 3.481 třísloupcový modul jako Doklady a Pokladna —
                    výšku drží modul a scrollují sloupce. Na mobilu připnutá
                    k výřezu, rezervu pro plovoucí lištu si bere dovnitř scrollu. */}
                {activeTab === 'banka' && (
                    <div className="flex flex-col"
                        style={isMobile
                            ? { position: 'fixed', zIndex: 0, top: 'var(--hlavicka-mobil)', left: 0, right: 0, bottom: 0, overflow: 'hidden' }
                            : { height: '100%', minHeight: 0 }}>
                        <FinanceBanka cardStyle={cardStyle} isDark={isDark} isMobile={isMobile}
                            rok={rok} mesic={mesic} setRok={setRok} setMesic={setMesic}
                            rezervaDole={isMobile ? REZERVA_LISTY : null}
                            onOpenDoklad={otevriDokladZBanky} />
                    </div>
                )}
                {activeTab === 'aop' && (
                    <FinanceAop
                        rok={rok} setRok={setRok}
                        variantaId={variantaId} setVariantaId={setVariantaId}
                        isAdmin={muze(user, 'finance.aop', 'upravuje')} cardStyle={cardStyle} isDark={isDark}
                    />
                )}
                {/* Pokladna je třísloupcový modul (`standard-rozvrzeni.md`) —
                    musí vyplnit výšku a scrollovat uvnitř sloupců, ne celou
                    stránkou. Stejný obal jako DPH nad ním.

                    Na mobilu jde obsahem až k hranám displeje jako Suroviny —
                    seznam je tam navigace celé obrazovky, ne karta plovoucí nad
                    stránkou. Řeší to **připnutí k výřezu**, ne rušení odsazení
                    rodičů zápornými okraji: `50% − 50vw` vychází ze
                    předpokladu, že rodičův obsah je v okně vycentrovaný, a
                    `<main>` má vlastní `marginLeft` podle sidebaru, takže blok
                    ujel a ořízl se z obou stran. Stejně je připnutý receptář
                    (`Spizirna.jsx`), který na to v komentáři rovnou upozorňuje.
                    `zIndex: 0` drží modul pod hlavičkou a nad září; rezervu pro
                    plovoucí lištu si bere dovnitř scrollu. */}
                {activeTab === 'pokladna' && (
                    <div className="flex flex-col"
                        style={isMobile
                            ? { position: 'fixed', zIndex: 0, top: 'var(--hlavicka-mobil)', left: 0, right: 0, bottom: 0, overflow: 'hidden' }
                            : { height: '100%', minHeight: 0 }}>
                        <PokladnaModul user={user} isDark={isDark} isMobile={isMobile}
                            rezervaDole={isMobile ? REZERVA_LISTY : null} />
                    </div>
                )}
                {/* Dodavatelé jsou třísloupcový modul jako Doklady a Pokladna —
                    musí vyplnit výšku a scrollovat uvnitř sloupců, ne celou
                    stránkou. Na mobilu jde obsahem až k hranám displeje;
                    rezervu pro plovoucí lištu si bere dovnitř scrollu. */}
                {activeTab === 'dodavatele' && (
                    <div className="flex flex-col"
                        style={isMobile
                            ? { position: 'fixed', zIndex: 0, top: 'var(--hlavicka-mobil)', left: 0, right: 0, bottom: 0, overflow: 'hidden' }
                            : { height: '100%', minHeight: 0 }}>
                        <DodavateleTab cardStyle={cardStyle} isDark={isDark}
                            initialDodavatelId={initialDodavatelId} onNavigate={onNavigate}
                            rezervaDole={isMobile ? REZERVA_LISTY : null} />
                    </div>
                )}

            </div>
        </div>
    );
}
